/**
 * First-party, privacy-safe analytics plus consent-gated GA4.
 * Without consent: an in-memory session id only, nothing stored on the device.
 * With consent: a persistent visitor id and GA4 (Consent Mode v2).
 * Never stores IP, names, e-mail, phone or typed text. Fails silently.
 */
import { botLabel, classifySource, normalizePage, type AnalyticsEvent } from "@/lib/analytics-shared";

export const CONSENT_KEY = "goproprojects.consent";
const VISITOR_KEY = "goproprojects.visitor";
const ENDPOINT = "/api/public/analytics";
const PREVIEW_HOST = /lovable\.app$|lovableproject\.com$|localhost|127\.0\.0\.1/;

export type Consent = "granted" | "denied" | null;

type Payload = {
  event: AnalyticsEvent;
  page: string;
  lang: string | null;
  label?: string | null;
  itemId?: string | null;
  props?: Record<string, string | number | boolean>;
  t: string;
};

const state = {
  sessionId: "",
  source: "" as string,
  interacted: false,
  queue: [] as Payload[],
  timer: 0 as unknown as ReturnType<typeof setTimeout> | 0,
  gaLoaded: false,
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function randomId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : String(Math.random()).slice(2);
}

export function readConsent(): Consent {
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

/** Admins (signed-in Supabase session), the preview and test modes are never measured. */
export function trackingDisabled(): boolean {
  if (typeof window === "undefined") return true;
  if (PREVIEW_HOST.test(window.location.hostname) && !window.location.search.includes("analytics-test")) return true;
  if (/^\/(admin|auth)(\/|$)/.test(window.location.pathname)) return true;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i) ?? "";
      if (/^sb-.*-auth-token$/.test(key)) return true;
    }
  } catch {
    /* storage blocked: keep measuring anonymously */
  }
  return false;
}

export function markInteracted() {
  state.interacted = true;
}

function ensureSession() {
  if (!state.sessionId) {
    state.sessionId = randomId();
    const params = new URLSearchParams(window.location.search);
    state.source = classifySource(document.referrer, window.location.hostname, params.get("utm_source"));
  }
}

function visitorId(): string | null {
  if (readConsent() !== "granted") return null;
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = randomId();
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function device(): string {
  const w = window.innerWidth;
  return w <= 640 ? "mobile" : w <= 1024 ? "tablet" : "desktop";
}

function flush() {
  state.timer = 0;
  if (!state.queue.length) return;
  const events = state.queue.splice(0, 40);
  const body = JSON.stringify({
    sessionId: state.sessionId,
    visitorId: visitorId(),
    source: state.source,
    device: device(),
    bot: botLabel({ userAgent: navigator.userAgent, webdriver: Boolean(navigator.webdriver), interacted: state.interacted }),
    interacted: state.interacted,
    events,
  });
  try {
    const blob = new Blob([body], { type: "application/json" });
    if (!navigator.sendBeacon?.(ENDPOINT, blob)) {
      void fetch(ENDPOINT, { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } }).catch(() => undefined);
    }
  } catch {
    /* fail silently */
  }
}

export function flushNow() {
  if (state.timer) clearTimeout(state.timer);
  flush();
}

export function trackEvent(event: AnalyticsEvent, extra: Omit<Payload, "event" | "page" | "lang" | "t"> = {}) {
  if (trackingDisabled()) return;
  ensureSession();
  const path = window.location.pathname;
  const lang = /^\/(nl|en|fr)(?=\/|$)/.exec(path)?.[1] ?? null;
  state.queue.push({ event, page: normalizePage(path), lang, t: new Date().toISOString(), ...extra });
  if (!state.timer) state.timer = setTimeout(flush, 2000);
  if (state.gaLoaded && window.gtag) {
    if (event === "page_view") {
      window.gtag("event", "page_view", { page_location: window.location.origin + path, page_path: path });
    } else {
      window.gtag("event", event, { label: extra.label ?? undefined, ...(extra.props ?? {}) });
    }
  }
}

/** Loads gtag.js only after consent. No measurement ID configured: nothing happens. */
export function applyConsent(consent: Consent) {
  const id = import.meta.env["VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY"] as string | undefined;
  if (!id || trackingDisabled()) return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params -- gtag.js needs the Arguments object
      window.dataLayer!.push(arguments);
    };
    window.gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    });
  }
  if (consent !== "granted") {
    window.gtag("consent", "update", { analytics_storage: "denied" });
    return;
  }
  window.gtag("consent", "update", { analytics_storage: "granted" });
  if (state.gaLoaded) return;
  state.gaLoaded = true;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
  window.gtag("set", "developer_id.dZjgwMW", true);
  window.gtag("js", new Date());
  window.gtag("config", id, {
    anonymize_ip: true,
    send_page_view: false,
    page_location: window.location.origin + window.location.pathname,
  });
  window.gtag("event", "page_view", { page_location: window.location.origin + window.location.pathname });
}

export function saveConsent(consent: "granted" | "denied") {
  try {
    window.localStorage.setItem(CONSENT_KEY, consent);
    if (consent === "denied") window.localStorage.removeItem(VISITOR_KEY);
  } catch {
    /* ignore */
  }
  applyConsent(consent);
  window.dispatchEvent(new CustomEvent("goproprojects:consent", { detail: consent }));
}
